import { planGraceDays, planInDunning } from "../_shared/plan-billing-frequency.ts";
import { bogotaDaysOverdue, graceExpired } from "../_shared/billing-day.ts";

type Json = Record<string, unknown>;

export type BillingAccessBlockReason =
  | "manual_block"
  | "authorization_lost"
  | "prepaid_expired"
  | "overdue"
  | "access_state_unavailable"
  | "cash_payment_required";

export interface BillingAccessOutcome {
  restored: boolean;
  ai_active: boolean;
  reason: BillingAccessBlockReason | null;
  state: string | null;
  limit_reached: boolean;
  subscription_reactivated: boolean;
  message: string;
}

const BLOCKING_SUSPENSIONS = new Set(["authorization_lost", "prepaid_expired", "overdue"]);

export function billingAccessBlockReason(access: Json | null | undefined): BillingAccessBlockReason | null {
  if (!access || typeof access.state !== "string") return "access_state_unavailable";
  if (access.state === "manual_block") return "manual_block";
  if (access.state === "suspended" && typeof access.reason === "string" && BLOCKING_SUSPENSIONS.has(access.reason)) {
    return access.reason as BillingAccessBlockReason;
  }
  return null;
}

export function billingAccessMessage(outcome: Pick<BillingAccessOutcome, "restored" | "reason" | "limit_reached">): string {
  switch (outcome.reason) {
    case "manual_block":
      return "La cuenta sigue pausada: tiene un bloqueo manual. Quítalo para que la IA vuelva a responder.";
    case "authorization_lost":
      return "La cuenta sigue pausada: el cliente canceló en Mercado Pago su autorización de cobro por uso. La IA vuelve cuando autorice de nuevo el cobro.";
    case "prepaid_expired":
      return "La cuenta sigue pausada: su plan prepagado está vencido. Actualiza la fecha de vencimiento para reactivarla.";
    case "overdue":
      return "La cuenta sigue pausada: tiene ciclos vencidos fuera del periodo de gracia. Registra el pago o condona el ciclo para reactivarla.";
    case "access_state_unavailable":
      return "No pudimos verificar ni actualizar el acceso de la cuenta: no se reactivó. Intenta nuevamente.";
    case "cash_payment_required":
      return "La cuenta está pausada. Registra el pago en efectivo o aplica una excepción para reactivarla.";
  }
  const base = outcome.restored
    ? "La cuenta quedó reactivada: la IA responde con los asistentes que estén encendidos."
    : "La cuenta está activa: la IA responde con los asistentes que estén encendidos.";
  return outcome.limit_reached
    ? `${base} Ojo: alcanzó su límite de conversaciones y la IA no responderá hasta que mejore su plan, recargue saldo o empiece el siguiente ciclo.`
    : base;
}

// deno-lint-ignore no-explicit-any
export async function reactivateSuspendedCashSubscription(admin: any, orgId: string): Promise<boolean> {
  const { data: sub } = await admin
    .from("subscriptions")
    .select("id, status, payment_mode, manual_block")
    .eq("organization_id", orgId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (!sub || sub.status !== "suspended" || sub.payment_mode !== "cash" || sub.manual_block) return false;

  const { error } = await admin
    .from("subscriptions")
    .update({ status: "active", reason: null, updated_at: new Date().toISOString() })
    .eq("id", sub.id)
    .eq("status", "suspended");
  if (error) {
    console.error(`[super-admin-api] reactivate subscription ${sub.id} failed:`, error.message);
    return false;
  }

  await admin.from("organizations").update({ ai_active: true }).eq("id", orgId);
  return true;
}

function outcome(fields: Omit<BillingAccessOutcome, "message">): BillingAccessOutcome {
  return { ...fields, message: billingAccessMessage(fields) };
}

// deno-lint-ignore no-explicit-any
export async function restoreBillingAccess(admin: any, orgId: string): Promise<BillingAccessOutcome> {
  const { data: org } = await admin.from("organizations").select("ai_active").eq("id", orgId).maybeSingle();
  const wasActive = org?.ai_active !== false;

  const { data: access, error } = await admin.rpc("_billing_access_state", { p_org_id: orgId, p_effective_date: null });
  if (error) console.error(`[super-admin-api] _billing_access_state ${orgId} failed:`, String(error?.message ?? ""));
  const state = error ? null : (access ?? null) as Json | null;
  const reason = billingAccessBlockReason(state);
  const limitReached = state?.reason === "limit_reached";
  const stateName = typeof state?.state === "string" ? state.state : null;

  if (reason) {
    return outcome({ restored: false, ai_active: wasActive, reason, state: stateName, limit_reached: limitReached, subscription_reactivated: false });
  }

  const subscriptionReactivated = await reactivateSuspendedCashSubscription(admin, orgId);
  if (!wasActive && !subscriptionReactivated) {
    const { error: updateError } = await admin.from("organizations").update({ ai_active: true }).eq("id", orgId);
    if (updateError) {
      console.error(`[super-admin-api] restore ai_active ${orgId} failed:`, String(updateError?.message ?? ""));
      return outcome({
        restored: false,
        ai_active: false,
        reason: "access_state_unavailable",
        state: stateName,
        limit_reached: limitReached,
        subscription_reactivated: false,
      });
    }
  }

  return outcome({
    restored: !wasActive || subscriptionReactivated,
    ai_active: true,
    reason: null,
    state: stateName,
    limit_reached: limitReached,
    subscription_reactivated: subscriptionReactivated,
  });
}

// deno-lint-ignore no-explicit-any
export async function liftManualBlock(admin: any, orgId: string): Promise<BillingAccessOutcome> {
  const { error } = await admin.from("organizations").update({ manual_block: false }).eq("id", orgId);
  if (error) {
    console.error(`[super-admin-api] lift manual_block ${orgId} failed:`, String(error?.message ?? ""));
    const { data: org } = await admin.from("organizations").select("ai_active").eq("id", orgId).maybeSingle();
    return outcome({
      restored: false,
      ai_active: org?.ai_active !== false,
      reason: "access_state_unavailable",
      state: null,
      limit_reached: false,
      subscription_reactivated: false,
    });
  }
  return restoreBillingAccess(admin, orgId);
}


// deno-lint-ignore no-explicit-any
export async function cashSwitchAccess(admin: any, orgId: string, now: Date = new Date()): Promise<BillingAccessOutcome> {
  const { data: org } = await admin
    .from("organizations")
    .select("ai_active, manual_block, plan, billing_override_until, billing_exempt")
    .eq("id", orgId)
    .maybeSingle();
  const { data: latest } = await admin
    .from("subscriptions")
    .select("manual_block")
    .eq("organization_id", orgId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  const aiActive = org?.ai_active !== false;
  const paused = (reason: BillingAccessBlockReason) =>
    outcome({ restored: false, ai_active: aiActive, reason, state: null, limit_reached: false, subscription_reactivated: false });

  if (org?.manual_block === true || latest?.manual_block === true) return paused("manual_block");
  if (!aiActive) return paused("cash_payment_required");

  const notPaused = { restored: false, ai_active: true, reason: null, state: null, limit_reached: false, subscription_reactivated: false, message: "" };
  if (!org?.plan || org.billing_exempt === true) return notPaused;

  const { data: plan } = await admin.from("plans").select("*").eq("id", org.plan).maybeSingle();
  if (!planInDunning(plan)) return notPaused;

  const overrideUntil = org.billing_override_until ? new Date(org.billing_override_until).getTime() : null;
  if (overrideUntil !== null && overrideUntil > now.getTime()) return notPaused;

  const { data: sub } = await admin
    .from("subscriptions")
    .select("next_billing_date")
    .eq("organization_id", orgId)
    .in("status", ["active", "pending"])
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (!sub?.next_billing_date) return notPaused;

  return graceExpired(bogotaDaysOverdue(sub.next_billing_date, now), planGraceDays(plan))
    ? paused("cash_payment_required")
    : notPaused;
}
